import { useRef } from 'react';
import { useQuery, useMutation, useQueryClient } from '@tanstack/react-query';
import type { UpdateAttachmentLabelsRequest } from '@vagusx/clients';
import { getEmailClient } from '@/lib/api-clients';
import { queryKeys } from '@/lib/query-keys';

/**
 * Hook to fetch an email by ID.
 * Polls when processing status is pending/processing.
 * Invalidates related queries (attachments, extractions, tasks) when processing completes.
 */
export function useEmail(id: string | undefined) {
  const queryClient = useQueryClient();
  const wasProcessing = useRef(false);

  return useQuery({
    queryKey: queryKeys.emails.detail(id ?? ''),
    queryFn: () => getEmailClient().getById(id!),
    enabled: !!id,
    refetchInterval: (query) => {
      const status = query.state.data?.processingStatus;
      const isProcessing = status === 'pending' || status === 'processing';

      if (isProcessing) {
        wasProcessing.current = true;
        return 2000;
      }

      // Processing just finished — invalidate related queries
      if (wasProcessing.current && status === 'completed') {
        wasProcessing.current = false;
        queryClient.invalidateQueries({ queryKey: queryKeys.emails.attachments(id ?? '') });
        queryClient.invalidateQueries({ queryKey: queryKeys.documents.all });
        queryClient.invalidateQueries({ queryKey: queryKeys.tasks.all });
      }

      return false;
    },
  });
}

/**
 * Hook to fetch attachments for an email.
 */
export function useEmailAttachments(emailId: string | undefined) {
  return useQuery({
    queryKey: queryKeys.emails.attachments(emailId ?? ''),
    queryFn: () => getEmailClient().getAttachments(emailId!),
    enabled: !!emailId,
  });
}

/**
 * Get the API streaming URL for an email attachment file.
 * No signed URLs — file is streamed through the API.
 */
export function getEmailAttachmentFileUrl(attachmentId: string): string {
  return getEmailClient().getAttachmentFileUrl(attachmentId);
}

/**
 * Hook to replay an email (delete derived data + re-process).
 */
export function useReplayEmail() {
  const queryClient = useQueryClient();
  return useMutation({
    mutationFn: (id: string) => getEmailClient().replay(id),
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: queryKeys.emails.all });
      queryClient.invalidateQueries({ queryKey: queryKeys.tasks.all });
      queryClient.invalidateQueries({ queryKey: queryKeys.documents.all });
    },
  });
}

/**
 * Hook to update labels on an attachment. Records the change as a correction
 * and triggers server-side recompute of the parent task's labels.
 */
export function useUpdateAttachmentLabels() {
  const queryClient = useQueryClient();
  return useMutation({
    mutationFn: ({
      attachmentId,
      data,
    }: {
      attachmentId: string;
      data: UpdateAttachmentLabelsRequest;
    }) => getEmailClient().updateAttachmentLabels(attachmentId, data),
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: queryKeys.emails.all });
      queryClient.invalidateQueries({ queryKey: queryKeys.tasks.all });
    },
  });
}
